import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardHeader,
  Grid,
  Divider,
  Breadcrumbs,
  Typography,
  Link,
} from '@material-ui/core';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import CloseIcon from '@material-ui/icons/Close';
import AddCircleOutlineOutlinedIcon from '@material-ui/icons/AddCircleOutlineOutlined';
import FilterListIcon from '@material-ui/icons/FilterList';
import {
  FilterChip,
  QuickSearch,
  useAPI,
  DeleteModal,
  VerticalBoxContainer,
  PageContentWrapper,
} from '@shared/components';
import PageHeader from '../../components/PageHeader';

import ExcelExportButton from '../../components/ExcelExportButton';
import { handleChipDelete } from '../../utils/filterHelpers';
import useTheme from '../../hooks/useTheme';
import useGlobalAlertContext from '../../hooks/useGlobalAlertContext';
import axios from '../../utils/axios';
import Table from './Table';
import Modal from './Modal';
import Filter from './Filter';

const fields = [
  {
    label: 'Šifra',
    value: 'code',
  },
  {
    label: 'Naziv',
    value: 'name',
  },
  {
    label: 'Tip atributa',
    value: 'attributeTypeName',
  },
];

const getLabelText = (field) => {
  const map = {
    name: 'Naziv',
    code: 'Šifra',
    attributeTypeName: 'Tip atributa',
  };
  return map[field];
};

const getChipValue = (chip, filters) => {
  return filters[chip];
};

const Attributes = () => {
  const theme = useTheme();
  const history = useHistory();
  const [attribute, setAttribute] = useState(null);
  const [filters, setFilters] = useState({});
  const [sorters, setSorters] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [triggerRerender, setRerender] = useState(false);

  const [modalVisible, setModalVisible] = useState(false);
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const { sendSuccessAlertData, sendErrorAlertData } = useGlobalAlertContext();

  const [attributes = { nodes: [], totalCount: 0 }, error, loading] = useAPI(
    () =>
      axios
        .get(`attributes`, {
          params: {
            limit: rowsPerPage,
            offset: page * rowsPerPage,
            conditions: filters,
            orderBy: sorters,
          },
        })
        .then(({ data }) => data),
    [page, rowsPerPage, filters, sorters, triggerRerender],
  );

  const handleFilterChange = (filterValues) => {
    setPage(0);
    setFilters({ ...filterValues });
  };

  return (
    <VerticalBoxContainer>
      <Filter
        currentValues={filters}
        visible={filterModalVisible}
        setVisible={setFilterModalVisible}
        handleFilterChange={handleFilterChange}
        getLabelText={getLabelText}
        getChipValue={getChipValue}
        setFilters={setFilters}
        setPage={setPage}
      />
      <Modal
        attribute={attribute}
        setAttribute={setAttribute}
        visible={modalVisible}
        setVisible={setModalVisible}
        setRerender={setRerender}
        sendSuccessAlertData={sendSuccessAlertData}
        sendErrorAlertData={sendErrorAlertData}
      />
      <DeleteModal
        buttonText="Obriši atribut"
        dataLength={attributes.totalCount}
        message="Uspješno obrisan atribut"
        page={page}
        setPage={setPage}
        objectId={attribute?.id}
        setObject={setAttribute}
        path="attributes"
        promptText={`Da li ste sigurni da želite obrisati atribut naziva ${attribute?.name}?`}
        resetObject={() => setAttribute(undefined)}
        sendSuccessAlertData={sendSuccessAlertData}
        sendErrorAlertData={sendErrorAlertData}
        setRerender={setRerender}
        visible={deleteModalVisible}
        setVisible={setDeleteModalVisible}
        title={`Obriši atribut sa šifrom ${attribute?.code}`}
        theme={theme}
        axios={axios}
      />
      <PageHeader
        title="Atributi"
        extra={
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddCircleOutlineOutlinedIcon />}
            onClick={() => setModalVisible(true)}
          >
            <Box fontSize="small">Novi atribut</Box>
          </Button>
        }
      />
      <PageContentWrapper>
        <Breadcrumbs style={{ paddingBottom: `${theme.spacing(5)}px` }}>
          <Link variant="body1" color="inherit" to="/" component={RouterLink}>
            Početna
          </Link>
          <Typography variant="body1" color="textPrimary">
            Šifarnici
          </Typography>
          <Typography variant="body1" color="textPrimary">
            Atributi
          </Typography>
        </Breadcrumbs>
        <Card>
          <CardHeader
            title={
              <QuickSearch
                name="name"
                placeholder="Brza pretraga po nazivu"
                setFilters={setFilters}
                mainFilter={filters}
                setPage={setPage}
              />
            }
            action={
              <Grid container spacing={4}>
                <Grid item>
                  <Button
                    color="primary"
                    disabled={Object.keys(filters).filter((k) => filters[k])?.length === 0}
                    startIcon={<CloseIcon fontSize="small" />}
                    onClick={() => setFilters({})}
                  >
                    <Box fontSize="small">Ukloni sve filtere</Box>
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<FilterListIcon fontSize="small" />}
                    onClick={() => setFilterModalVisible(true)}
                  >
                    <Box fontSize="small">Filteri</Box>
                  </Button>
                </Grid>
                <Grid item>
                  <ExcelExportButton
                    totalCount={attributes.totalCount}
                    fields={fields}
                    sorters={sorters}
                    filters={filters}
                    variant="contained"
                    routeName="attributes"
                    fileName="Atributi"
                  />
                </Grid>
              </Grid>
            }
          />
          <Divider />
          {Object.keys(filters).filter((k) => filters[k])?.length !== 0 && (
            <>
              <Box ml={2} p={2} display="flex" alignItems="center" flexWrap="wrap">
                {Object.keys(filters).map(
                  (chip) =>
                    filters[chip] !== undefined && (
                      <FilterChip
                        color="primary"
                        variant="outlined"
                        key={chip}
                        label={`${getLabelText(chip)}: ${getChipValue(chip, filters)}`}
                        onDelete={() => handleChipDelete(chip, setFilters, setPage)}
                      />
                    ),
                )}
              </Box>
            </>
          )}
          <Table
            loading={loading}
            error={error}
            attributes={attributes.nodes}
            page={page}
            setPage={setPage}
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            totalCount={attributes.totalCount}
            sorters={sorters}
            setSorters={setSorters}
            setAttribute={setAttribute}
            setModalVisible={setModalVisible}
            setDeleteModalVisible={setDeleteModalVisible}
            history={history}
          />
        </Card>
      </PageContentWrapper>
    </VerticalBoxContainer>
  );
};

export default Attributes;
